import { useSignIn } from "@clerk/clerk-react";
import { useNavigate } from "@tanstack/react-router";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button/button";
import { BwChevronLeftLine, BwLoadingLine } from "@/components/ui/icons";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input/input-otp";
import { APP_ROUTERS } from "@/constants/app-routers";
import { PRESALE_EVENTS, useAnalytics } from "@/libs/analytics";
import { usePresaleMembership } from "./_api/use-presale-membership";
import { PresaleFunnelLayout } from "./_components/funnel/presale-funnel-layout";
import { PresaleResendCode } from "./_components/funnel/presale-resend-code";
import { getPresaleAnalyticsProps } from "./_libs/presale-analytics-props";
import {
  keepCampaignSearch,
  type PresaleVerifySearch,
} from "./_libs/presale-funnel";

const CODE_LENGTH = 6;
const OTP_SLOT_CLASS = "border-border-dark-low data-[active=true]:ring-[1px]";
/** 1-based slot positions — a stable key per box, not an array index. */
const OTP_SLOTS = Array.from({ length: CODE_LENGTH }, (_, i) => i + 1);

/**
 * Step 1b — the emailed code that finishes the account.
 *
 * Clerk's first factor was started on the previous screen; `emailAddressId` rides the
 * URL so a resend can re-prepare the same factor. On success the session is activated
 * and the funnel moves to the home step still carrying the campaign params.
 */
export function PresaleVerifyPage({ search }: { search: PresaleVerifySearch }) {
  const navigate = useNavigate();
  const { signIn, setActive } = useSignIn();
  const { sendEvent } = useAnalytics();
  const { data: membership } = usePresaleMembership(search.checkoutLinkId);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const verify = async () => {
    setIsVerifying(true);
    setError("");
    try {
      if (!signIn) throw new Error("Sign in not initialized");
      const result = await signIn.attemptFirstFactor({
        strategy: "email_code",
        code,
      });
      if (result?.status !== "complete") {
        throw new Error("Verification is not complete");
      }
      await setActive({ session: result.createdSessionId });

      sendEvent(PRESALE_EVENTS.EMAIL_VERIFIED, {
        ...getPresaleAnalyticsProps({ search, membership }),
        stepId: "email_verified",
        stepName: "Email Verified",
        email: search.email,
      });

      navigate({
        to: APP_ROUTERS.PRESALE_PLACE,
        search: keepCampaignSearch(search),
        replace: true,
      });
    } catch {
      setError("This code is invalid or has expired. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  // A 6-digit code is a complete answer — submit it without a second click. The
  // `isVerifying` guard keeps a paste-then-edit from starting a second concurrent
  // `attemptFirstFactor`/`setActive`; `error` is cleared in `onChange`, which is what
  // correctly re-arms this for a corrected code.
  useEffect(() => {
    if (code.length !== CODE_LENGTH || error || isVerifying) return;
    void verify();
  }, [code]);

  const resend = async () => {
    if (!signIn || !search.emailAddressId) return;
    try {
      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: search.emailAddressId,
      });
    } catch (resendError) {
      console.error(resendError);
    }
  };

  return (
    <PresaleFunnelLayout
      step="account"
      back={
        <Button
          kind="soft"
          variant="neutral"
          size="xs"
          onClick={() =>
            navigate({
              to: APP_ROUTERS.PRESALE_SIGN_UP,
              search: keepCampaignSearch(search),
            })
          }
        >
          <BwChevronLeftLine size={16} className="stroke-dark-max" />
          Back
        </Button>
      }
      title={
        search.firstName
          ? `Welcome back, ${search.firstName}!`
          : "Check your email"
      }
      description={
        <>
          Enter the {CODE_LENGTH}-digit code we sent to{" "}
          <span className="text-text-primary">{search.email}</span>.
        </>
      }
    >
      <InputOTP
        pattern={REGEXP_ONLY_DIGITS}
        name="code"
        maxLength={CODE_LENGTH}
        value={code}
        onChange={(value) => {
          setCode(value);
          if (error) setError("");
        }}
      >
        <InputOTPGroup>
          {OTP_SLOTS.map((slot) => (
            <InputOTPSlot
              key={slot}
              index={slot - 1}
              className={OTP_SLOT_CLASS}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>

      {error && <p className="mt-2 text-sm text-text-negative">{error}</p>}
      <PresaleResendCode onResend={resend} />

      <Button
        disabled={code.length < CODE_LENGTH || isVerifying}
        onClick={verify}
        type="submit"
        kind="solid"
        size="xl"
        className="mt-6 w-full"
      >
        {isVerifying && <BwLoadingLine size={20} className="animate-spin" />}
        {isVerifying ? "Verifying..." : "Verify"}
      </Button>
    </PresaleFunnelLayout>
  );
}
